<template>
  <div class="sales-return">
    <!-- Quick periods -->
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 5">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Customer</label>
          <FormDropdown v-model="filters.customer" :options="customerOptions" placeholder="All customers" clearable />
        </div>
        <div class="rp-filter">
          <label>Reason</label>
          <FormDropdown v-model="filters.reason" :options="reasonOptions" placeholder="All reasons" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Item / Voucher</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input
              v-model="filters.search"
              type="text"
              class="form-control"
              placeholder="Search item, return or invoice no."
            />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-red">
        <span class="rp-card-icon"><i class="ti ti-receipt-refund"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Returns</div>
          <div class="rp-card-value">{{ formatInt(summary.return_count) }}</div>
          <div class="rp-card-sub">
            on {{ plural(summary.invoice_count, 'invoice') }} · {{ plural(summary.customer_count, 'customer') }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-package-import"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Quantity Returned</div>
          <div class="rp-card-value">{{ formatQty(summary.qty) }}</div>
          <div class="rp-card-sub">{{ plural(summary.product_count, 'product') }} · stock value {{ formatMoney(summary.cost) }}</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-cash-banknote"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Refund</div>
          <div class="rp-card-value text-danger">{{ formatMoney(summary.refund) }}</div>
          <div class="rp-card-sub">{{ summary.return_rate }}% of {{ formatMoney(summary.sales_net) }} sales</div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-arrows-split-2"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Paid Back · Due Adjusted</div>
          <div class="rp-card-value">{{ formatMoney(summary.cash_refund) }}</div>
          <div class="rp-card-sub">{{ formatMoney(summary.due_adjusted) }} set against customer due</div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Refunds {{ summary.trend_unit === 'month' ? 'by month' : 'by day' }}</span>
          <span class="rp-legend">
            <span><i class="rp-dot out"></i>Refund</span>
            <span><i class="rp-dot" :style="{ background: COUNT_COLOR }"></i>Quantity</span>
          </span>
        </div>
        <div v-show="summary.trend.length" class="rp-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0">No returns in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">By reason</div>
        <div v-if="reasonBars.length" class="rp-bars">
          <div v-for="reason in reasonBars" :key="reason.reason">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2" :class="{ 'text-muted fst-italic': !reason.recorded }" :title="reason.reason">
                {{ reason.reason }}
              </span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(reason.refund) }} <span class="text-muted fw-normal">· {{ formatQty(reason.qty) }}</span>
              </span>
            </div>
            <div class="rp-bar-track">
              <div class="rp-bar-fill" :class="reason.recorded ? 'out' : ''" :style="{ width: `${reason.percent}%` }"></div>
            </div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No returns in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Most returned products</div>
        <ul v-if="summary.top_items.length" class="rp-ranked">
          <li v-for="(item, index) in summary.top_items" :key="item.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ formatMoney(item.refund) }} refunded · {{ plural(item.count, 'return') }}</small>
            </div>
            <span class="rp-chip out">{{ formatQty(item.qty) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No returns in this period.</p>
      </div>
    </div>

    <!-- Returns table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold text-body">
          <i class="ti ti-list-details me-1"></i>Sales returns
          <span class="rp-pill-count ms-1">{{ formatInt(rows.length) }}</span>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button
                    type="button"
                    class="rp-expand"
                    :title="expanded[row.key] ? 'Hide items' : 'Show items'"
                    @click="toggleRow(row.key)"
                  >
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td class="text-nowrap">{{ formatDate(row.date) }}</td>
                <td class="fw-semibold text-nowrap">{{ row.return_no }}</td>
                <td class="text-nowrap">
                  {{ row.invoice_no || '—' }}
                  <span v-if="row.invoice_date" class="rp-sub">sold {{ formatDate(row.invoice_date) }}</span>
                </td>
                <td>{{ row.customer }}</td>
                <td class="text-center"><span class="rp-count">{{ row.item_count }}</span></td>
                <td class="text-end fw-semibold">{{ formatQty(row.qty) }}</td>
                <td class="text-end fw-semibold text-danger">{{ formatMoney(row.refund) }}</td>
                <td>
                  {{ row.refund_via }}
                  <span v-if="Number(row.due_adjusted) && Number(row.cash_refund)" class="rp-sub">
                    {{ formatMoney(row.cash_refund) }} + {{ formatMoney(row.due_adjusted) }} due
                  </span>
                </td>
                <td>
                  <template v-if="row.reasons.length">
                    <span v-for="reason in row.reasons" :key="reason" class="rp-badge muted me-1 mb-1">{{ reason }}</span>
                  </template>
                  <span v-else class="text-muted fst-italic small">Not recorded</span>
                </td>
                <td class="text-muted">{{ row.created_by || '—' }}</td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title d-flex flex-wrap justify-content-between gap-2">
                      <span>
                        <i class="ti ti-arrow-back-up me-1"></i>Return {{ row.return_no }}
                        <template v-if="row.invoice_no"> · against invoice {{ row.invoice_no }}</template>
                      </span>
                      <span class="text-muted fw-normal">
                        Stock value put back {{ formatMoney(row.cost) }}
                      </span>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Item Code</th>
                            <th>Item Name</th>
                            <th>Variant</th>
                            <th>Batch</th>
                            <th class="text-end">Qty</th>
                            <th class="text-end">Unit Refund</th>
                            <th class="text-end">Discount</th>
                            <th class="text-end">VAT</th>
                            <th class="text-end">Refund</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="line in row.lines" :key="line.key">
                            <td class="fw-semibold">{{ line.item_code }}</td>
                            <td>
                              {{ line.item_name }}
                              <small v-if="line.sku" class="d-block text-muted">SKU: {{ line.sku }}</small>
                            </td>
                            <td>
                              <template v-if="line.variant">
                                <span v-for="part in line.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                              </template>
                              <span v-else class="text-muted">—</span>
                            </td>
                            <td>{{ line.batch_no || '—' }}</td>
                            <td class="text-end fw-semibold">
                              {{ formatQty(line.qty) }}<small v-if="line.unit" class="text-muted ms-1">{{ line.unit }}</small>
                            </td>
                            <td class="text-end">{{ formatMoney(line.unit_price) }}</td>
                            <td class="text-end">{{ Number(line.discount) ? formatMoney(line.discount) : '—' }}</td>
                            <td class="text-end">{{ Number(line.vat) ? formatMoney(line.vat) : '—' }}</td>
                            <td class="text-end fw-semibold">{{ formatMoney(line.refund) }}</td>
                            <td>
                              <span v-if="line.reason">{{ line.reason }}</span>
                              <span v-else class="text-muted fst-italic">Not recorded</span>
                            </td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr>
                            <th colspan="4" class="text-end">Return total</th>
                            <th class="text-end">{{ formatQty(row.qty) }}</th>
                            <th></th>
                            <th class="text-end">{{ formatMoney(row.discount) }}</th>
                            <th class="text-end">{{ formatMoney(row.vat) }}</th>
                            <th class="text-end">{{ formatMoney(row.refund) }}</th>
                            <th></th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-receipt-refund"></i>
                  <div class="fw-semibold">No returns found</div>
                  <small class="text-muted">Try another period, reason or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th colspan="4" class="text-end">Total ({{ plural(rows.length, 'return') }})</th>
              <th class="text-center">{{ formatInt(summary.product_count) }}</th>
              <th class="text-end">{{ formatQty(summary.qty) }}</th>
              <th class="text-end text-danger">{{ formatMoney(summary.refund) }}</th>
              <th colspan="3"></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Refund = what the customer paid for the returned units (after discount, incl. VAT). The reason is entered on
      the Sales Return form; returns saved before it was added show "Not recorded".
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { COUNT_COLOR, RETURN_COLOR } from './salesReportMixin';

const EMPTY_SUMMARY = {
  refund: 0, discount: 0, vat: 0, cost: 0, qty: 0, cash_refund: 0, due_adjusted: 0, return_count: 0,
  invoice_count: 0, customer_count: 0, product_count: 0, sales_net: 0, return_rate: 0, by_reason: [],
  top_items: [], trend_unit: 'day', trend: [],
};

export default {
  name: 'SalesReturnReport',
  mixins: [inventoryReport, periodReport, salesReport],

  reportUrl: apiUrls.sales_return_report.get,
  fileName: 'sales_return_register',
  columns: [
    { key: 'date', label: 'Date' },
    { key: 'return_no', label: 'Return No' },
    { key: 'invoice_no', label: 'Invoice' },
    { key: 'customer', label: 'Customer' },
    { key: 'item_count', label: 'Items', class: 'text-center', numeric: true },
    { key: 'qty', label: 'Qty', class: 'text-end', numeric: true },
    { key: 'refund', label: 'Refund', class: 'text-end', numeric: true },
    { key: 'refund_via', label: 'Refund Via' },
    { key: 'reason', label: 'Reason' },
    { key: 'created_by', label: 'By' },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), customer: null, reason: null,
        warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: 'date',
      sortDir: 'desc',
      COUNT_COLOR,
    };
  },

  computed: {
    reasonOptions() {
      return (this.options.reasons || []).map((r) => ({ id: r.id, text: r.name }));
    },

    hasActiveFilters() {
      return this.periodChanged || this.itemFiltersActive || !!this.filters.customer || !!this.filters.reason;
    },

    reasonBars() {
      return this.withPercent(this.summary.by_reason, 'refund');
    },

    visibleRows() {
      return this.sortRows(this.rows);
    },
  },

  watch: {
    'filters.reason'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      return {
        ...this.periodParams(),
        ...this.customerParams(),
        ...(this.filters.reason ? { reason: this.filters.reason } : {}),
      };
    },

    resetExtraFilters() {
      return { ...defaultPeriod(this.today), customer: null, reason: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => this.drawMoneyTrend(this.$refs.trendChart, [
        { label: 'Refund', field: 'refund', color: RETURN_COLOR },
        { label: 'Quantity', field: 'qty', color: COUNT_COLOR, count: true },
      ]));
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => {
        const items = r.lines.map((l) => `${escapeHtml(l.item_name)}${l.variant ? ` (${escapeHtml(l.variant)})` : ''} × ${this.formatQty(l.qty)}`).join('<br>');
        return `
        <tr>
          <td>${this.formatDate(r.date)}</td>
          <td>${escapeHtml(r.return_no)}</td>
          <td>${escapeHtml(r.invoice_no)}</td>
          <td>${escapeHtml(r.customer)}</td>
          <td><small>${items}</small></td>
          <td class="num">${this.formatQty(r.qty)}</td>
          <td class="num">${this.formatMoney(r.refund)}</td>
          <td>${escapeHtml(r.refund_via)}</td>
          <td>${escapeHtml(r.reason || 'Not recorded')}</td>
        </tr>`;
      }).join('');
      const table = `<table><thead><tr><th>Date</th><th>Return No</th><th>Invoice</th><th>Customer</th>
          <th>Products</th><th class="num">Qty</th><th class="num">Refund</th><th>Refund Via</th><th>Reason</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="5" class="num">Total</td><td class="num">${this.formatQty(s.qty)}</td>
          <td class="num">${this.formatMoney(s.refund)}</td><td colspan="2"></td></tr></tfoot></table>`;
      this.openPrint(
        'Sales Return Register',
        [
          [this.periodText(), this.customerText(),
            `Reason: ${this.optionName(this.options.reasons || [], this.filters.reason, 'All')}`,
            ...this.itemFiltersText()].join(' · '),
          `Printed ${new Date().toLocaleString()}`,
        ],
        [
          ['Returns', `${this.formatInt(s.return_count)} · ${this.plural(s.invoice_count, 'invoice')}`],
          ['Quantity Returned', this.formatQty(s.qty)],
          ['Refund', `${this.formatMoney(s.refund)} (${s.return_rate}% of sales)`],
          ['Paid Back · Due Adjusted', `${this.formatMoney(s.cash_refund)} · ${this.formatMoney(s.due_adjusted)}`],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
